import { AfterViewInit, ChangeDetectorRef, Component, ElementRef, EventEmitter, Inject, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import {
  MAT_DIALOG_DATA as MAT_DIALOG_DATA,
  MatDialog as MatDialog,
  MatDialogRef as MatDialogRef,
} from '@angular/material/dialog';
import { Attachment, Markup } from '../../models';
import { saveAs } from 'file-saver';
import * as JSZip from 'jszip';
import { MarkupEditorConfig, MarkupSaveResult } from '../markup-editor/markup-editor.types';
import { MarkupService } from '../../services/markup.service';
import { PdfRenderService } from '../../services/pdf-render.service';
import { Store } from '@ngrx/store';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { appDataDetails } from '../../core/shared.selectors';
import { photoProofOf, verifiedThresholdM } from '../../helpers/photo-proof';
import * as pdfjsLib from 'pdfjs-dist';

@Component({
    selector: 'app-carousel',
    templateUrl: './carousel.component.html',
    styleUrls: ['./carousel.component.scss'],
    standalone: false
})
export class CarouselComponent implements OnInit, AfterViewInit, OnDestroy {
  @Input() public attachments: Attachment[] = [];
  @Input() public initialSlide = 0;
  @Input() public isDialog = true;
  @Output() public onClose: EventEmitter<any> = new EventEmitter<any>();
  @ViewChild('swiperEl') swiperEl: ElementRef;

  public downloadingAll = false;
  public showMarkupEditor = false;
  public markupEditorConfig: MarkupEditorConfig;
  public markupPreviews: { [id: string]: string } = {};
  public pdfPageState: { [id: string]: { currentPage: number; totalPages: number; loading: boolean; previousPage?: number } } = {};
  public pdfPageImageUrls: { [key: string]: string } = {};
  public pdfMarkupPreviews: { [key: string]: string } = {};
  public pdfHasMarkups: { [id: string]: boolean } = {};
  public markedUpUrls: { [id: string]: string } = {};
  private pdfObjectUrls: string[] = [];
  private editingAttachment: Attachment = null;
  private editingAttachmentIndex: number = -1;
  private pdfDocs: { [id: string]: pdfjsLib.PDFDocumentProxy } = {};
  public editingPageNumber = false;
  public pageJumpInput = '';
  /** Photo proof (GeoStamp): the details panel beside the picture, and the org threshold. */
  public proofPanelOpen = false;
  public verifiedThresholdM = verifiedThresholdM(null);
  /** Per picture: the viewer shows the untouched original instead of the stamped copy. */
  public showOriginal: { [id: string]: boolean } = {};
  private readonly destroyed$ = new Subject<void>();

  constructor(
    public dialogRef: MatDialogRef<CarouselComponent>,
    @Inject(MAT_DIALOG_DATA) public inputData: any,
    public dialog: MatDialog,
    private markupService: MarkupService,
    private pdfRenderService: PdfRenderService,
    private cdr: ChangeDetectorRef,
    private store: Store<any>,
  ) {
  }

  hasProof(att: any): boolean {
    return !!photoProofOf(att);
  }

  toggleProofPanel() {
    this.proofPanelOpen = !this.proofPanelOpen;
    this.cdr.detectChanges();
  }

  toggleOriginal(att: any) {
    this.showOriginal[att.id] = !this.showOriginal[att.id];
    this.cdr.detectChanges();
  }

  /** The stamped copy (GeoStamp) is what the viewer shows when one exists, unless the viewer asked for the original. */
  private displayUrl(att: any): string {
    if (att?.stamped_url && !this.showOriginal[att.id]) {
      return att.stamped_url;
    }
    return att.preview || att.preview_url;
  }

  public isEditable = true;

  ngOnInit(): void {
    this.store.select(appDataDetails).pipe(takeUntil(this.destroyed$)).subscribe((data: any) => {
      this.verifiedThresholdM = verifiedThresholdM(data);
    });
    if (this.isDialog) {
      const { attachments, initialSlide, isEditable } = this.inputData;
      this.attachments = attachments;
      this.initialSlide = initialSlide;
      this.isEditable = isEditable !== false; // default true for backward compat
    }

    // Fetch page counts for any PDF documents
    this.attachments.forEach(att => this.initPdfPages(att));

    // Fetch markup previews for image attachments so the latest
    // marked-up preview is shown even after the carousel is recreated
    this.attachments.forEach(att => this.initImageMarkupPreview(att));
  }

  ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
    this.pdfObjectUrls.forEach(url => URL.revokeObjectURL(url));
    this.pdfObjectUrls = [];
  }

  ngAfterViewInit(): void {
    if (this.initialSlide > 0 && this.swiperEl?.nativeElement?.swiper) {
      setTimeout(() => {
        this.swiperEl.nativeElement.swiper.slideTo(this.initialSlide, 0);
      });
    }

    // Listen for Swiper slide changes to re-apply markup previews via DOM
    // Swiper web component manages its own DOM, so Angular bindings on <img>
    // inside slides may not persist across slide transitions.
    if (this.swiperEl?.nativeElement) {
      this.swiperEl.nativeElement.addEventListener('swiperslidechange', () => {
        this.applyMarkupPreviews();
      });
    }
  }

  get showDownloadAll(): boolean {
    const downloadableItems = this.attachments.filter(att => this.isDownloadable(att));
    return downloadableItems.length > 1;
  }

  isDownloadable(attachment: Attachment): boolean {
    if (attachment?.['fileType'] === 'picture') return true;
    if (attachment?.['fileType'] === 'document') return true;
    if (attachment?.['fileType'] === 'video' && (attachment?.['download_url'] || attachment?.original_url)) return true;
    return false;
  }

  download(attachment: Attachment) {
    if (attachment?.['fileType'] === 'picture') {
      saveAs(
        this.markedUpUrls[attachment.id] || (attachment as any).marked_up_url || attachment.original_url || attachment.preview,
        attachment.original_name || 'image',
      );
      return;
    }

    if (attachment?.['fileType'] === 'document') {
      saveAs(
        this.markedUpUrls[attachment.id] || (attachment as any).marked_up_url || attachment.original_url,
        attachment.original_name || 'document.pdf',
      );
      return;
    }

    if (attachment?.['fileType'] === 'video') {

      // Check for explicit download_url first, then S3 original for attachment-based videos
      const downloadUrl = (attachment as any).download_url || attachment.original_url;
      if (downloadUrl) {
        const name = attachment.original_name?.includes('.')
          ? attachment.original_name
          : `${attachment.original_name || 'video'}.mp4`;
        // cache: 'no-store' avoids reusing the <video> player's cached response,
        // which lacks CORS headers (S3 only sends them when Origin is present)
        fetch(downloadUrl, { cache: 'no-store' })
          .then(resp => resp.blob())
          .then(blob => saveAs(blob, name))
          .catch(err => {
            console.error('Video download failed, opening in new tab', err);
            window.open(downloadUrl, '_blank');
          });
        return;
      }

      // If no download_url, video doesn't have static renditions enabled
      console.warn('No download_url available. Video needs static renditions enabled in Mux.', attachment);
      alert('This video cannot be downloaded. It was uploaded without download support enabled.');
      return;
    }
  }

  async downloadAll() {
    const downloadable = this.attachments.filter(att => this.isDownloadable(att));

    if (!downloadable.length) {
      alert('No downloadable media found. Videos need download support enabled during upload.');
      return;
    }

    this.downloadingAll = true;

    try {
      const zip = new JSZip();
      const promises = [];
      const fileNameCounter = {};

      // Process each image
      downloadable.forEach((attachment, index) => {
        let url: string;
        let defaultName: string;
        if (attachment?.['fileType'] === 'picture') {
          url = this.markedUpUrls[attachment.id] || (attachment as any).marked_up_url || attachment.original_url || attachment.preview || attachment.preview_url;
          defaultName = `image_${index + 1}.jpg`;
        } else if (attachment?.['fileType'] === 'document') {
          url = this.markedUpUrls[attachment.id] || (attachment as any).marked_up_url || attachment.original_url;
          defaultName = `document_${index + 1}.pdf`;
        } else {
          url = (attachment as any).download_url || attachment.original_url;
          defaultName = `video_${index + 1}.mp4`;
        }

        let filename = attachment.original_name || defaultName;

        // Handle duplicate filenames
        if (fileNameCounter[filename]) {
          const nameParts = filename.split('.');
          const extension = nameParts.pop();
          const baseName = nameParts.join('.');
          filename = `${baseName}_${fileNameCounter[filename]}.${extension}`;
          fileNameCounter[attachment.original_name]++;
        } else {
          fileNameCounter[filename] = 1;
        }

        // Fetch image as blob (no-store: cached media responses may lack CORS headers)
        const promise = fetch(url, { cache: 'no-store' })
          .then(response => {
            if (!response.ok) {
              throw new Error(`Failed to fetch ${filename}`);
            }
            return response.blob();
          })
          .then(blob => {
            // Add to zip
            zip.file(filename, blob);
          })
          .catch(error => {
            console.error(`Failed to download ${filename}:`, error);
          });

        promises.push(promise);
      });

      // Wait for all images to be fetched
      await Promise.all(promises);

      // Generate zip file
      const content = await zip.generateAsync({
        type: 'blob',
        compression: 'DEFLATE',
        compressionOptions: { level: 6 }
      });

      // Download zip file with descriptive name
      const timestamp = new Date().toISOString().split('T')[0];
      const fileCount = downloadable.length;
      saveAs(content, `media_${fileCount}_files_${timestamp}.zip`);

    } catch (error) {
      console.error('Error downloading all images:', error);
    } finally {
      this.downloadingAll = false;
    }
  }

  getSlideImageSrc(att: Attachment): string {
    // If it's a document with PDF page navigation active
    if (att?.['fileType'] === 'document' && this.pdfPageState[att.id]) {
      const state = this.pdfPageState[att.id];
      const key = `${att.id}_${state.currentPage}`;
      // Priority: markup preview > rendered page image > attachment preview as fallback
      if (this.pdfMarkupPreviews[key]) {
        return this.pdfMarkupPreviews[key];
      }
      if (this.pdfPageImageUrls[key]) {
        return this.pdfPageImageUrls[key];
      }
      // Keep showing the previous page's image while loading to avoid flash
      if (state.loading && state.previousPage) {
        const prevKey = `${att.id}_${state.previousPage}`;
        return this.pdfMarkupPreviews[prevKey] || this.pdfPageImageUrls[prevKey] || att.preview || att.preview_url;
      }
      return att.preview || att.preview_url;
    }
    // "View original" means the untouched photo: no stamp and no markup preview either.
    if (this.showOriginal[att.id]) {
      return att.preview || att.preview_url;
    }
    return this.markupPreviews[att.id] || this.displayUrl(att);
  }

  isPdf(att: Attachment): boolean {
    return att?.mime === 'application/pdf';
  }

  initPdfPages(att: Attachment) {
    if (att?.['fileType'] !== 'document' || !this.isPdf(att) || this.pdfPageState[att.id]) return;
    this.pdfPageState[att.id] = { currentPage: 1, totalPages: 1, loading: true };

    const pdfUrl = att.original_url;

    // Load PDF client-side + fetch markups in parallel
    Promise.all([
      pdfUrl ? this.pdfRenderService.loadDocument(pdfUrl).catch(() => null) : Promise.resolve(null),
      this.markupService.getByAttachment(att.id).toPromise().catch(() => []),
    ]).then(async ([pdfDoc, markups]) => {
      if (pdfDoc) {
        this.pdfDocs[att.id] = pdfDoc;
        this.pdfPageState[att.id].totalPages = pdfDoc.numPages;
      } else {
        // Fallback to API for page count if PDF.js can't load (CORS, etc.)
        const count = await this.markupService.getPdfPageCount(att.id).toPromise().catch(() => 1);
        this.pdfPageState[att.id].totalPages = count;
      }
      this.pdfPageState[att.id].loading = false;

      const hasMarkups = markups?.length > 0;
      this.pdfHasMarkups[att.id] = hasMarkups;
      if (hasMarkups) {
        (markups as Markup[]).forEach(m => {
          if (m.preview_url) {
            this.pdfMarkupPreviews[`${att.id}_${m.page_number}`] = m.preview_url;
          }
        });
      }
      // Always render page 1 if no markup preview exists for it
      if (!this.pdfMarkupPreviews[`${att.id}_1`]) {
        this.loadPdfPageImage(att, 1);
      }
      this.cdr.detectChanges();
    });
  }

  startPageJump(att: Attachment) {
    this.editingPageNumber = true;
    this.pageJumpInput = String(this.pdfPageState[att.id]?.currentPage || 1);
  }

  submitPageJump(att: Attachment) {
    const page = parseInt(this.pageJumpInput, 10);
    const state = this.pdfPageState[att.id];
    if (!isNaN(page) && state && page >= 1 && page <= state.totalPages) {
      this.goToPdfPage(att, page);
    }
    this.editingPageNumber = false;
  }

  cancelPageJump() {
    this.editingPageNumber = false;
  }

  async goToPdfPage(att: Attachment, page: number) {
    const state = this.pdfPageState[att.id];
    if (!state || page < 1 || page > state.totalPages || page === state.currentPage || state.loading) return;

    const key = `${att.id}_${page}`;
    // If we already have the image cached, switch immediately
    if (this.pdfMarkupPreviews[key] || this.pdfPageImageUrls[key]) {
      state.previousPage = state.currentPage;
      state.currentPage = page;
      this.cdr.detectChanges();
      return;
    }

    // Render the page first, THEN update currentPage
    state.loading = true;
    this.cdr.detectChanges();

    await this.loadPdfPageImage(att, page);

    state.previousPage = state.currentPage;
    state.currentPage = page;
    state.loading = false;
    this.cdr.detectChanges();
  }

  private async loadPdfPageImage(att: Attachment, page: number) {
    try {
      const pdfDoc = this.pdfDocs[att.id];
      if (pdfDoc) {
        const dataUrl = await this.pdfRenderService.renderPageAsDataUrl(pdfDoc, page, 1.5);
        this.pdfPageImageUrls[`${att.id}_${page}`] = dataUrl;
      } else {
        const blob = await this.markupService.getPdfPageImage(att.id, page).toPromise();
        const url = URL.createObjectURL(blob);
        this.pdfObjectUrls.push(url);
        this.pdfPageImageUrls[`${att.id}_${page}`] = url;
      }
    } catch {
      // Silent fail — page will show fallback
    }
  }

  private initImageMarkupPreview(att: Attachment) {
    if (!att?.isImage || att?.['fileType'] === 'document') return;
    this.markupService.getByAttachment(att.id, 1).subscribe({
      next: (markups: Markup[]) => {
        const markup = markups?.length ? markups[0] : null;
        if (markup?.preview_url) {
          this.markupPreviews[att.id] = markup.preview_url;
          this.cdr.detectChanges();
        }
      },
    });
  }

  openMarkupEditor(att: Attachment) {
    this.editingAttachment = att;
    this.editingAttachmentIndex = this.attachments.indexOf(att);

    const isDocument = att?.['fileType'] === 'document';

    // For documents (PDFs), use the rendered preview image, not the PDF file itself
    const imageUrl = isDocument
      ? (att.preview_url || att.thumb_url)
      : (att.original_url || att.preview || att.preview_url);

    if (isDocument) {
      // Fetch page count and existing markup in parallel
      Promise.all([
        this.markupService.getPdfPageCount(att.id).toPromise().catch(() => 1),
        this.markupService.getByAttachment(att.id, 1).toPromise().catch(() => []),
      ]).then(([pageCount, markups]) => {
        this.markupEditorConfig = {
          imageUrl,
          originalUrl: att.original_url,
          attachmentId: att.id,
          existingMarkup: markups?.length ? markups[0] : null,
          pageNumber: 1,
          totalPages: pageCount,
          isDocument: true,
          filename: att.original_name,
        };
        this.showMarkupEditor = true;
        this.dialogRef.disableClose = true;
      });
    } else {
      // Fetch existing markup from API before opening editor
      this.markupService.getByAttachment(att.id, 1).subscribe({
        next: (markups: Markup[]) => {
          this.markupEditorConfig = {
            imageUrl,
            originalUrl: att.original_url,
            attachmentId: att.id,
            existingMarkup: markups?.length ? markups[0] : null,
            pageNumber: 1,
            filename: att.original_name,
          };
          this.showMarkupEditor = true;
          this.dialogRef.disableClose = true;
        },
        error: () => {
          this.markupEditorConfig = {
            imageUrl,
            originalUrl: att.original_url,
            attachmentId: att.id,
            existingMarkup: null,
            pageNumber: 1,
            filename: att.original_name,
          };
          this.showMarkupEditor = true;
          this.dialogRef.disableClose = true;
        },
      });
    }
  }

  closeMarkupEditor() {
    this.showMarkupEditor = false;
    this.dialogRef.disableClose = false;
    this.markupEditorConfig = null;
  }

  onMarkupSaved(result: MarkupSaveResult) {
    const { markup, previewDataUrl, updatedPreviewUrl, updatedThumbUrl, markedUpUrl } = result;
    const att = this.editingAttachment;
    const displayUrl = previewDataUrl || updatedPreviewUrl;

    // Store marked-up URL for immediate download (without needing to reload)
    if (att && markedUpUrl) {
      this.markedUpUrls[att.id] = markedUpUrl;
    }

    // For PDFs, store preview keyed by attachmentId_pageNumber to avoid page order issues
    if (att && displayUrl) {
      const pageNumber = markup?.page_number || 1;
      if (att['fileType'] === 'document') {
        this.pdfMarkupPreviews[`${att.id}_${pageNumber}`] = displayUrl;
        if (!this.pdfHasMarkups[att.id]) {
          this.pdfHasMarkups[att.id] = true;
        }
        // Fetch all markups to pick up auto-saved pages the editor didn't emit events for
        this.markupService.getByAttachment(att.id).subscribe({
          next: (markups: Markup[]) => {
            (markups as Markup[]).forEach(m => {
              const key = `${att.id}_${m.page_number}`;
              // Don't overwrite the just-saved page's fresh data URL with the S3 URL
              if (m.preview_url && !this.pdfMarkupPreviews[key]) {
                this.pdfMarkupPreviews[key] = m.preview_url;
              }
            });
            this.cdr.detectChanges();
          },
        });
      } else {
        this.markupPreviews[att.id] = displayUrl;
      }
    }

    // Don't mutate att.preview_url / att.thumb_url — objects may be frozen by NgRx.
    // Instead, pass the data URL through the service notification.

    this.editingAttachment = null;
    this.editingAttachmentIndex = -1;
    this.closeMarkupEditor();
    this.cdr.detectChanges();

    // Notify other components (e.g. pictures-section with OnPush) to refresh thumbnails
    if (att) {
      this.markupService.notifyMarkupSaved(att.id, displayUrl);
    }
  }

  /**
   * Walk every Swiper slide and patch any <img> whose attachment has a
   * markup preview stored in the local map. Called after save and on every
   * slide transition so the preview is always visible.
   */
  private applyMarkupPreviews() {
    // Disabled - Angular binding via getSlideImageSrc should handle this
    return;
  }

  close(event) {
    if (this.onClose) {
      event.stopPropagation();
      this.onClose.emit();
    }
  }
}
